import React from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { Block, ChildBlock } from '@staty/shared/schemas/block';
import { BlockView } from '@staty/shared/components/BlockRenderer';
import useEditorStore, { ColAddr, EntityRef } from './editorStore';
import BlockFrame from './BlockFrame';
import BlockInserter from './BlockInserter';
import RichtextBlockEditor from './blocks/RichtextBlockEditor';
import ImageBlockEditor from './blocks/ImageBlockEditor';
import EmbedBlockEditor from './blocks/EmbedBlockEditor';
import ButtonBlockEditor from './blocks/ButtonBlockEditor';
import ColumnsBlockEditor from './blocks/ColumnsBlockEditor';

/**
 * An editable, sortable list of blocks — the post body (top level) or one
 * column cell (`colAddr` set). Each block renders inside a BlockFrame with
 * the type's editor; "+" inserters sit between blocks.
 *
 * Drag reorders WITHIN this list only (dnd-kit SortableContext per list;
 * nested column cells are separate contexts). Cross-container drag is
 * deliberately out of scope — see ADR 0033.
 */

interface Props {
  entity: EntityRef;
  blocks: (Block | ChildBlock)[];
  leagueName: string;
  colAddr?: ColAddr;
}

export default function BlockList({ entity, blocks, leagueName, colAddr }: Props) {
  const selectedBlockId = useEditorStore((s) => s.selectedBlockId);
  const selectBlock = useEditorStore((s) => s.selectBlock);
  const updateBlock = useEditorStore((s) => s.updateBlock);
  const insertBlock = useEditorStore((s) => s.insertBlock);
  const removeBlock = useEditorStore((s) => s.removeBlock);
  const duplicateBlock = useEditorStore((s) => s.duplicateBlock);
  const moveBlock = useEditorStore((s) => s.moveBlock);

  // Distance activation so plain clicks (select, caret placement) never
  // start a drag — the drag handle needs 6px of travel first.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = blocks.findIndex((b) => b.id === active.id);
    const to = blocks.findIndex((b) => b.id === over.id);
    if (from === -1 || to === -1) return;
    moveBlock(entity, from, to, colAddr);
  };

  const inColumn = Boolean(colAddr);

  return (
    <div className={inColumn ? 'canvas-blocklist canvas-blocklist--column' : 'canvas-blocklist'}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
          {blocks.map((block, i) => (
            <React.Fragment key={block.id}>
              <BlockInserter
                leagueName={leagueName}
                allowColumns={!inColumn}
                onInsert={(b) => insertBlock(entity, i, b, colAddr)}
              />
              <BlockFrame
                blockId={block.id}
                selected={selectedBlockId === block.id}
                onSelect={() => selectBlock(block.id)}
                onMoveUp={i > 0 ? () => moveBlock(entity, i, i - 1, colAddr) : null}
                onMoveDown={i < blocks.length - 1 ? () => moveBlock(entity, i, i + 1, colAddr) : null}
                onDuplicate={() => duplicateBlock(entity, block.id, colAddr)}
                onDelete={() => removeBlock(entity, block.id, colAddr)}
              >
                <BlockBody
                  block={block}
                  entity={entity}
                  leagueName={leagueName}
                  colAddr={colAddr}
                  selected={selectedBlockId === block.id}
                  onChange={(patch) => updateBlock(entity, block.id, patch, colAddr)}
                />
              </BlockFrame>
            </React.Fragment>
          ))}
          <BlockInserter
            leagueName={leagueName}
            allowColumns={!inColumn}
            onInsert={(b) => insertBlock(entity, blocks.length, b, colAddr)}
          />
        </SortableContext>
      </DndContext>
    </div>
  );
}

interface BodyProps {
  block: Block | ChildBlock;
  entity: EntityRef;
  leagueName: string;
  colAddr?: ColAddr;
  selected: boolean;
  onChange: (patch: Partial<Block>) => void;
}

function BlockBody({ block, entity, leagueName, colAddr, selected, onChange }: BodyProps) {
  switch (block.type) {
    case 'richtext':
      return (
        <RichtextBlockEditor
          blockId={block.id}
          html={block.html}
          onChange={(html) => onChange({ html })}
        />
      );
    case 'image':
      return (
        <ImageBlockEditor
          block={block}
          selected={selected}
          leagueName={leagueName}
          onChange={onChange}
        />
      );
    case 'embed':
      return <EmbedBlockEditor block={block} selected={selected} onChange={onChange} />;
    case 'button':
      return <ButtonBlockEditor block={block} onChange={onChange} />;
    case 'columns':
      // Only reachable at the top level (inserters inside columns omit the
      // Columns entry), so colAddr is never set here.
      return <ColumnsBlockEditor block={block} entity={entity} leagueName={leagueName} />;
    case 'divider':
    default:
      // Divider (and any future type): the shared renderer as-is; the frame
      // provides selection/deletion.
      return <BlockView block={block} />;
  }
}
